import { useCallback, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Input, Button, message } from "antd";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { MdOutlineImage, MdDeleteOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import useUser from "../hooks/useUser";
import { useStoreWrapper } from "../../../store/store";
import { useIncidentList } from "../IncidentsList/utils";
import { Select } from "../analytics/fall-analysis/form/sharedImports";

const { TextArea } = Input;

const validationSchema = Yup.object().shape({
  category: Yup.string().required("Category is required"),
  description: Yup.string().required("Description is required"),
  facility: Yup.string().required("Facility is required"),
});

type FileWithPreview = File & { preview: string };

const SupportRequestForm = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const user = useStoreWrapper((state) => state.user);
  const { userFacilities } = useIncidentList();

  const userName = user.firstName + " " + user.lastName;
  const userEmail = user.email;
  const loggedInUserId = user.userId;

  const { addSupportTicket } = useUser();
  const { mutate: logSupportTicket, isLoading } = addSupportTicket;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.map(
        (file): FileWithPreview =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
      ),
    ]);
  }, []);

  useEffect(() => {
    if (files.length > 2) {
      toast.error("You can only upload 2 files");
    }
  }, [files]);

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    maxFiles: 2,
  });

  const initialValues = {
    name: "",
    email: "",
    category: "",
    description: "",
    incident: "",
    facility: "",
  };

  const onSubmit = async (
    values: any,
    {
      setSubmitting,
      resetForm,
      setFieldValue,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      resetForm: () => void;
      setFieldValue: (field: string, value: any) => void;
    }
  ) => {
    try {
      // prepare ticket data
      const ticketData = {
        Description__c: values.description,
        Issue_Category__c: values.category,
        Priority__c: values.priority,
        Requester__c: loggedInUserId,
        Incident__c: values.incident,
        Facility__c: values.facility,
      };
      // prepare Attachments
      const attachments = await Promise.all(
        files.map(async (file) => {
          const base64 = (await convertToBase64(file)) as string;
          return {
            name: file.name,
            uploadedBy: loggedInUserId,
            data: base64.split(",")[1],
          };
        })
      );
      // log the ticket
      logSupportTicket(
        { ticket: ticketData, attachments },
        {
          onSuccess: (res) => {
            toast.success(res.data.message);
            resetForm();
            setFiles([]);
          },
          onError: (error: any) => {
            console.error("Error submitting support form:", error);
            toast.error(error.response.data.message);
          },
          onSettled: () => {
            setSubmitting(false);
          },
        }
      );
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      message.error(
        "An error occurred while preparing your request. Please try again."
      );
      setSubmitting(false);
    }
  };

  const convertToBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) =>
        reject(new Error("Error converting to base64"));
    });
  };
  const facilityOptions = (userFacilities ?? [])
    .map((facility: any) => ({
      label: facility.facilityName,
      value: facility.facilityId,
    }))
    .sort((a: any, b: any) => (a.label > b.label ? 1 : -1));
  const categoryOptions = [
    { value: "General inquiry", label: "General inquiry" },
    { value: "Assistance", label: "Assistance" },
    { value: "Bug", label: "Bug" },
    { value: "Recommendation", label: "Recommendation" },
    { value: "New Feature Request", label: "New Feature Request" },
  ];
  return (
    <div className="bg-white mx-auto max-w-6xl p-8">
      <div className="flex flex-col md:w-full md:flex-row md:justify-between">
        <h1 className="mb-6 text-3xl font-bold text-purple-500">
          Request for support
        </h1>
        <div className="md:w-[47.5%] lg:w-[48.5%]">
          <p className="mb-6 text-purple-300">
            Please check the "Help" section, as many of your issues may already
            be addressed.
          </p>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, dirty, setFieldValue }) => (
          <Form className="flex flex-col space-y-4">
            <div className="flex flex-col md:flex-row md:space-x-8">
              <div className="flex-1 space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Name
                  </label>
                  <Field
                    name="name"
                    as={Input}
                    placeholder="Enter your full name"
                    className="mt-1"
                    value={userName}
                    disabled
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email address
                  </label>
                  <Field
                    name="email"
                    as={Input}
                    placeholder="Enter your email"
                    className="mt-1"
                    value={userEmail}
                    disabled
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="facility"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Facility
                  </label>

                  <Select
                    name="facility"
                    placeholder="Select Facility"
                    className="mt-1"
                    options={facilityOptions}
                    onChange={(val: any) => {
                      const value = val?.value;
                      setFieldValue("facility", value);
                    }}
                  />

                  <ErrorMessage
                    name="facility"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="incident"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Incident (Optional)
                  </label>
                  <Field
                    name="incident"
                    as={Input}
                    placeholder="Enter incident number (if any)"
                    className="mt-1"
                  />
                  <ErrorMessage
                    name="incident"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Category
                  </label>
                  <Select
                    name="category"
                    placeholder="Select issue category"
                    className="mt-1"
                    options={categoryOptions}
                    onChange={(val: any) => {
                      const value = val?.value;
                      setFieldValue("category", value);
                    }}
                  />
                  <ErrorMessage
                    name="category"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Describe your issue
                  </label>
                  <Field
                    name="description"
                    as={TextArea}
                    rows={4}
                    placeholder="Tell us about your experiences"
                    className="mt-1"
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>
              </div>

              <div className="mt-7 flex-1 md:mt-0">
                <div>
                  <label
                    htmlFor="attachment"
                    className="block text-sm font-medium text-gray-400"
                  >
                    Attachment (Max 2 files, 5MB each)
                  </label>
                  <div
                    {...getRootProps()}
                    className="mt-1 flex h-48 cursor-pointer flex-col items-center justify-center border-2 border-dashed border-gray-300 p-4 text-center"
                  >
                    <input {...getInputProps()} name="attachment" />
                    {isDragActive ? (
                      <p>Drop the files here ...</p>
                    ) : (
                      <div className="flex flex-col items-center justify-center">
                        <MdOutlineImage className="size-11 text-4xl text-purple-200" />
                        <p className="text-sm text-gray-400">
                          Drag and drop some files here, or click to select
                          files
                        </p>
                      </div>
                    )}
                  </div>
                  {files.length > 0 && (
                    <div className="mt-4 flex flex-col gap-2">
                      <h4 className="text-lg font-bold">Files:</h4>
                      <ul className="pl-0">
                        {files.map((file) => (
                          <li
                            key={file.name}
                            className="mb-3 flex items-center border-b border-solid border-gray-200 p-3"
                          >
                            <img
                              src={file.preview}
                              alt={file.name}
                              className="mr-2 h-14 w-14"
                            />
                            <span className="mr-5 text-sm text-gray-400">
                              {file.name} - {file.size} bytes
                            </span>
                            <MdDeleteOutline
                              onClick={() => handleRemoveFile(file)}
                              className="cursor-pointer text-red-500"
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="default"
                onClick={() => navigate("/user/dashboard")}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={
                  isSubmitting ||
                  isLoading ||
                  !(isValid && dirty) ||
                  files.length > 2
                }
                className={`${
                  isSubmitting || isLoading || !(isValid && dirty)
                    ? "cursor-not-allowed opacity-50"
                    : "bg-purple-700 hover:bg-purple-800"
                } transition-all duration-300 ease-in-out`}
                loading={isLoading}
              >
                Send Request
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SupportRequestForm;
